import { useQuery } from '@tanstack/react-query';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { getLiveWeightDisplays } from '../../services/weighbridges/weighbridgesService';

/** Delphi Frame.WeightDisplay poll interval. */
export const LIVE_WEIGHT_POLL_MS = 2500;

/**
 * LED color matching GenWS Frame.WeightDisplay:
 * error → black (light) / white (dark), out of range → red,
 * stable → lime (darker in light mode for contrast),
 * unstable → yellow (darker in light mode for contrast).
 */
export function liveWeightLedColor({ isError, inRange, stable }, isDark) {
  if (isError) return isDark ? '#ffffff' : '#111111';
  if (!inRange) return '#e53935';
  if (stable) return isDark ? '#76ff03' : '#2e7d32';
  return isDark ? '#ffee58' : '#f9a825';
}

export function formatLiveWeight(weight, units) {
  const n = Number(weight);
  const value = Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—';
  const u = (units || '').trim();
  return u ? `${value} ${u}` : value;
}

export function liveWeightStatusTip(item) {
  if (item?.isError) return item.errorMessage || 'Display error';
  if (!item?.inRange) return 'Out of range';
  if (item?.stable) return 'Stable';
  return 'Unstable';
}

/**
 * @param {{ item: object, compact?: boolean, labelOverride?: string|null }} props
 */
export function LiveWeightTile({ item, compact = false, labelOverride = null }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const color = liveWeightLedColor(item, isDark);
  const label = labelOverride || item.description || `Weighbridge ${item.weighbridgeId}`;
  const tip = liveWeightStatusTip(item);

  return (
    <Tooltip title={`${label} — ${tip}${item.method ? ` (${item.method})` : ''}`}>
      <Box
        sx={{
          flex: compact ? '0 0 auto' : '1 1 140px',
          minWidth: compact ? 0 : 120,
          maxWidth: compact ? 200 : 220,
          px: compact ? 1 : 1.5,
          py: compact ? 0.35 : 0.75,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          alignItems: compact ? 'baseline' : 'stretch',
          gap: compact ? 1 : 0.25,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ fontWeight: 600, letterSpacing: 0.2, maxWidth: compact ? 110 : '100%' }}
        >
          {label}
        </Typography>
        <Typography
          variant={compact ? 'subtitle1' : 'h6'}
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontWeight: 700,
            lineHeight: 1.1,
            color,
            textShadow: item.isError ? 'none' : `0 0 8px ${color}66`,
            whiteSpace: 'nowrap',
          }}
        >
          {formatLiveWeight(item.weight, item.units)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * App-wide live weight strip (up to 6 indicators). Polls only while mounted.
 */
export default function LiveWeightStrip() {
  const { data = [], isFetching, isError, error } = useQuery({
    queryKey: ['weighbridges', 'live-display'],
    queryFn: ({ signal }) => getLiveWeightDisplays(signal),
    refetchInterval: LIVE_WEIGHT_POLL_MS,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 0,
  });

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        alignItems: 'stretch',
        px: 0,
        pb: 1.5,
        minHeight: 64,
      }}
    >
      {data.length === 0 && !isError && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {isFetching ? 'Reading live weights…' : 'No weighbridges configured for live display.'}
        </Typography>
      )}
      {isError && (
        <Typography variant="body2" color="error" sx={{ py: 1 }}>
          {error?.message || 'Failed to read live weights.'}
        </Typography>
      )}
      {data.map((item) => (
        <LiveWeightTile key={item.weighbridgeId} item={item} />
      ))}
    </Box>
  );
}
